import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, } from '@angular/core';
import {
  ComponentsWithConfigsList,
  ComponentWithConfigsObject,
} from '../../../../configuration/componentsWithConfigs.datamodel';
import {
  ConfigurationModalComponent
} from '../../../../publish/component/form/configuration/forms/configurationFormModal.component';
import {
  ConfigurationBuilder,
  ConfigurationFn,
  ConfigurationObject,
} from '../../../../configuration/store/configuration.datamodel';
import { ConfigurationService } from '../../../../configuration/store/configuration.service';
import { LSSEntityObject } from '../../lssEntity.datamodel';
import { AlertActionType, AlertListObject, } from '../../../../alert/alert.datamodel';
import { CoreService } from '../../../../core/store/core.service';
import { ConfigLabelPipe } from '../../../pipes/config-label.pipe';
import { OpenModalService } from '../../../../core/services/openModal.service';
import { GetSelectedConfigurationPipe } from './get-selected-configuration.pipe';
import { ParameterContentValue } from '../configuration-parameters/parameters-group/parameters.model';
import { PermissionsType } from '../../../../user/permissions/permissions.datamodel';
import { DeploymentService } from '../../../../deployments/store/deployment.service';
import { ComponentType, GLOBAL_CONFIG_COMPONENT_TYPES } from '../../../../publish/component/store/component.datamodel';
import { AppService } from '../../../../application/app.service';
import { ActorType } from '../../../datamodel';
import { PermissionsService } from '../../../../user/permissions/permissions.service';
import { UserProfile } from '../../../../user/user.datamodel';
import { UserService } from '../../../../user/user.service';
import { ActivationParametersService } from '../../../../activation-parameters/activation-parameters.service';

/**
 * Component representing the accordion for configurations
 */
@Component({
    selector: "ama-ng-configurations-accordion",
    templateUrl: "./configurations-accordion.component.html",
    standalone: false
})
export class ConfigurationsAccordionComponent implements OnChanges {
  @Input() cmpsWithConfigs!: ComponentsWithConfigsList;
  @Input() entitySelected!: LSSEntityObject;
  @Input() redefinedConfigSuccess!: string;
  @Input() selectedConfigurations!: {[key: string]: string};
  @Input() editConfiguration!: boolean;
  @Input() displayDynamicForm!: boolean;
  @Output() selectedConfigurationsChange = new EventEmitter<any>();

  user!: UserProfile;
  selectedConfig: ConfigurationObject[] = [];
  isSimpleViewActive = false;
  keepOpenIndex = -1;
  protected readonly PermissionsType = PermissionsType;

  private readonly configLabelPipe: ConfigLabelPipe = new ConfigLabelPipe();
  private readonly getSelectedConfigurationPipe = new GetSelectedConfigurationPipe(this.configurationService);
  private readonly ALERT_ACTION_TYPE = AlertActionType;

  constructor(
    private readonly configurationService: ConfigurationService,
    private readonly coreService: CoreService,
    readonly deploymentService: DeploymentService,
    readonly permissionsService: PermissionsService,
    readonly userService: UserService,
    private readonly openModalService: OpenModalService,
    private readonly activationParametersService: ActivationParametersService,
    private readonly appService: AppService,
  ) {
    this.user = this.userService.getUser();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.cmpsWithConfigs) {
      this.selectedConfig = [
        ...changes.cmpsWithConfigs.currentValue.components.map((cmp: ComponentWithConfigsObject) => {
          const config = cmp.configurations.find((conf: ConfigurationObject) => {
            if (this.editConfiguration) {
              return ConfigurationFn.isDefaultConfig(conf);
            }
            return conf.guid === this.selectedConfigurations?.[cmp.component.id];
          });
          const selectedConfig = config
            ? ConfigurationFn.build(config)
            : ConfigurationFn.build(cmp.configurations[0]);
          return this.configLabelPipe.transform(selectedConfig);
        }),
      ];
    }

    if (changes.displayDynamicForm) {
      this.isSimpleViewActive = changes.displayDynamicForm.currentValue;
    }
  }

  /**
   * Update selectConfigurations array when selecting a configuration
   * @param $event Selected configuration
   * @param compId Component Id
   * @param index Selected config index reference
   */
  onSelectConfiguration($event: any, compId: string, index: number) {
    this.selectedConfig[index] = ConfigurationFn.build({...$event});
    this.selectedConfigurationsChange.emit({
      ...this.selectedConfigurations,
      [compId]: $event.guid,
    });
    this.configurationService.clearSuccessMessage();
  }

  /**
   * Open the configuration form modal for a new configuration from default config
   * @param component the component to create the configuration for
   * @param index component index
   */
  createConfig(component: ComponentWithConfigsObject, index: number): void {
    const config = this.buildNewConfiguration(component);
    const parent = component.configurations[0];
    const configParameters = this.getSelectedConfigurationPipe.transform(component,
      { [component.component.id]: ConfigurationFn.findDefault(component.configurations)?.guid}
    );

    this.openConfigModal(config, parent, configParameters, false, component);
    this.keepOpenIndex = index;
  }

  /**
   * Open the configuration form modal for a new configuration from the selected configuration
   * @param component the component to create the configuration for
   * @param index component index
   */
  copyConfig(component: ComponentWithConfigsObject, index: number): void {
    const parent = component.configurations[0];
    const configParameters = this.getSelectedConfigurationPipe.transform(component, this.selectedConfigurations);
    const config = this.buildNewConfiguration(component, configParameters.value);

    this.openConfigModal(config, parent, configParameters, false, component);
    this.keepOpenIndex = index;
  }

  /**
   * Open the configuration form modal to update a configuration
   * @param component
   * @param index
   */
  editConfig(component: ComponentWithConfigsObject, index: number) {
    const configId = this.selectedConfigurations[component.component.id];
    const config = component.configurations.find(conf => conf.guid === configId);
    const parent = component.configurations[0];
    const configParameters = this.getSelectedConfigurationPipe.transform(component, this.selectedConfigurations);

    this.openConfigModal(config as ConfigurationObject, parent, configParameters, true, component);
    this.keepOpenIndex = index;
  }

  /**
   * Handle the click over the delete button
   *
   * @param component
   */
  handleDeleteApp(component: ComponentWithConfigsObject) {
    this.coreService.displayAlert(this.createDeleteAlertObject(component));
  }

  /**
   * Delete the configuration and select the default configuration
   *
   * @param configuration  the configuration to delete
   */
  deleteConfig(configuration: ConfigurationObject) {
    this.configurationService.selectConfiguration(
      configuration.componentId?.guid as string,
      configuration.parentGuid!
    );
    this.configurationService.deleteConfigurationAction(configuration.guid!);
  }

  onPanelChange() {
    this.configurationService.clearSuccessMessage();
  }

  /**
   * Returns a new configuration object
   * @param component
   * @param content
   * @private
   */
  private buildNewConfiguration(component: ComponentWithConfigsObject, content?: string) {
    this.configurationService.clearSuccessMessage();
    let actors;
    if (component.component.componentType === ComponentType.SECO_FARM_MAP) {
      actors = {actor: {actorName: "SELL_CONNECT", actorType: ActorType.PRODUCT}};
    } else {
      actors = this.permissionsService.isUserAuthorized(PermissionsType.MANAGE_MARKET_CATALOG) ?
        {actor: {actorName: this.entitySelected.label, actorType: ActorType.ORGANISATION}}
        : {
          actor: {actorName: this.user.officeId, actorType: ActorType.OFFICE},
          parentActor: {actorName: this.user.organizationId, actorType: ActorType.ORGANISATION}
        };
    }
    return ConfigurationFn.build({
      parentGuid: component.configurations[0].guid,
      componentId: {guid: component.component.id},
      content: content ?? component.configurations[0].content,
      ...actors,
    } as ConfigurationBuilder);
  }

  /**
   * Open the configuration form modal
   * @param configuration     the new configuration or configuration to update
   * @param parent            the parent configuration
   * @param configParameters  the configuration parameter values and template
   * @param edit               whether or not it's edit mode
   */
  private openConfigModal(
    configuration: ConfigurationObject,
    parent: ConfigurationObject,
    configParameters: ParameterContentValue,
    edit: boolean,
    component?: ComponentWithConfigsObject,
  ) {
    const isEligibleComponent = !!component && GLOBAL_CONFIG_COMPONENT_TYPES.includes(
      this.appService.getAppComponentsType([component.component])
    );
    const hasPermission =
      this.permissionsService.isUserAuthorized(PermissionsType.MANAGE_GLOBAL_CONFIGURATION);

    this.openModalService.openDialog(ConfigurationModalComponent, {
      modal: {size: "xl"},
      data: {
        configuration,
        parent,
        configParameters,
        edit,
        displayDynamicForm: this.displayDynamicForm,
        isSimpleViewActive: this.isSimpleViewActive,
        canCreateGlobalConfig: isEligibleComponent && hasPermission &&
          this.activationParametersService.isActive('MYA_GLOBAL_CONFIG_A'),
      },
    });
  }

  /**
   * Initialize the delete alert in case of configuration deletion
   *
   * @param component
   */
  private createDeleteAlertObject(
    component: ComponentWithConfigsObject
  ): AlertListObject {
    const config = component.configurations.find(
      (conf) =>
        conf.guid === this.selectedConfigurations[component.component.id]
    );
    return {
      title: "Delete a configuration",
      question:
        "All entities deployed with this configuration will no longer use it " +
        "and will be back to the default one. This action cannot be undone.",
      alerts: [
        {
          type: "W",
          message: `Are you sure you want to delete configuration "${config?.name}"?`,
          code: '',
        },
      ],
      actions: [
        {
          label: "Cancel",
          type: this.ALERT_ACTION_TYPE.SECONDARY,
          callback: () => undefined,
        },
        {
          label: "Delete",
          type: this.ALERT_ACTION_TYPE.PRIMARY,
          callback: this.deleteConfig.bind(this, config!),
        },
      ],
    };
  }
}
